import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useMemo } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const useApiClient = () => {
  const { getAccessToken, logout } = useAuth();

  const client = useMemo(() => {
    const instance = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    // Request interceptor - add auth token
    instance.interceptors.request.use(
      async (config) => {
        try {
          const token = await getAccessToken();
          if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
          }
          return config;
        } catch (error) {
          console.error('Failed to get access token:', error);
          // If token retrieval fails, log user out
          logout();
          return Promise.reject(error);
        }
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - handle auth failures
    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        // Check for authentication errors
        if (error.response?.status === 401 || error.response?.status === 403) {
          console.error('Authentication failed, logging out user');
          // Clear any stored tokens
          localStorage.removeItem('accessToken');
          // Log the user out
          logout();
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }, [getAccessToken, logout]);

  return client;
};

// Translation API methods
export const translationApi = {
  deleteTranslation: async (client, documentId) => {
    return await client.delete(`/translations/${documentId}`);
  },
  markReadyForApproval: async (client, documentId) => {
    return await client.patch(`/translations/${documentId}/ready-for-approval`);
  },
  clearRetranslationComments: async (client, documentId) => {
    return await client.delete(`/translations/${documentId}/retranslation/comments`);
  }
};

export default useApiClient;
